import React, { useEffect, useMemo, useState } from "react";
import {
  Box, Typography, TextField, Button, CircularProgress, Alert, Tabs, Tab, Paper, InputAdornment,
  MenuItem, Chip, FormControlLabel, Switch, Pagination, IconButton, Tooltip, Badge,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import SaveIcon from "@mui/icons-material/Save";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import adminApi from "../api/adminApi";

// Every text of the new website, in its six languages (BackendNode routes/siteTexts.js).
// The site's own texts come from <website>/texts/<lang>.json (written by its build); only the texts
// changed here are stored in the API. The website shows a change right away (it reads the changed
// texts in the browser) and puts it in the page HTML on its next build.
const LOCALES = [
  { code: "en", label: "English", path: "" },
  { code: "pt", label: "Português", path: "/pt" },
  { code: "es", label: "Español", path: "/es" },
  { code: "de", label: "Deutsch", path: "/de" },
  { code: "fr", label: "Français", path: "/fr" },
  { code: "pl", label: "Polski", path: "/pl" },
];
const SECTIONS = [
  { code: "", label: "All pages" },
  { code: "common", label: "Every page (menu, footer, search bar, buttons)" },
  { code: "home", label: "Home page" },
  { code: "fleet", label: "Fleet" },
  { code: "about", label: "About us" },
  { code: "contacts", label: "Contacts" },
  { code: "faq", label: "FAQ" },
  { code: "blog", label: "Blog" },
  { code: "booking", label: "Booking (cars, extras, details, payment, emails)" },
  { code: "legal", label: "Terms, privacy and cookies" },
  { code: "consent", label: "Cookie banner" },
];
const DEFAULT_SITE = "https://www.whynotcarrental.com";
const SITE_KEY = "site_texts_website";
const PAGE_SIZE = 40;

const errorText = (err, fallback) => err.response?.data?.message || err.message || fallback;

/** {{variables}} and <0>link</0> tags a text must keep: the site fills them in. */
const tokensOf = (text) =>
  [...String(text).matchAll(/\{\{\s*[\w.]+\s*\}\}|<\/?\d+>/g)].map((m) => m[0].replace(/\s+/g, "")).sort();
const tokensProblem = (original, value) => {
  const want = tokensOf(original);
  const have = tokensOf(value);
  if (want.join("|") === have.join("|")) return "";
  const missing = want.filter((t) => !have.includes(t));
  const extra = have.filter((t) => !want.includes(t));
  return [
    missing.length ? `Keep ${missing.join(" ")}` : "",
    extra.length ? `Remove ${extra.join(" ")}` : "",
  ].filter(Boolean).join(" · ");
};

const SiteTextEditor = () => {
  const [website, setWebsite] = useState(() => localStorage.getItem(SITE_KEY) || DEFAULT_SITE);
  const [defaults, setDefaults] = useState(null); // { lang: { key: text } }
  const [saved, setSaved] = useState({}); // { lang: { key: text } } as stored in the API
  const [edits, setEdits] = useState({}); // { lang: { key: text } } unsaved
  const [lang, setLang] = useState("pt");
  const [section, setSection] = useState("");
  const [search, setSearch] = useState("");
  const [onlyChanged, setOnlyChanged] = useState(false);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);

  const load = async (site) => {
    setLoading(true);
    setMessage(null);
    try {
      const base = site.replace(/\/+$/, "");
      const own = await Promise.all(
        LOCALES.map(async ({ code }) => {
          const res = await fetch(`${base}/texts/${code}.json`, { cache: "no-store" });
          if (!res.ok) throw new Error(`${base}/texts/${code}.json answered ${res.status}`);
          return [code, await res.json()];
        })
      );
      const stored = await Promise.all(
        LOCALES.map(async ({ code }) => [code, (await adminApi.get(`/site-texts/${code}`)).data.texts || {}])
      );
      setDefaults(Object.fromEntries(own));
      setSaved(Object.fromEntries(stored));
      setEdits({});
    } catch (err) {
      setDefaults(null);
      setMessage({
        severity: "error",
        text: `Could not read the website texts (${errorText(err, "error")}). Check the website address: it must be the new site, built after this feature.`,
      });
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(website); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const valueOf = (code, key) =>
    edits[code]?.[key] ?? saved[code]?.[key] ?? defaults?.[code]?.[key] ?? "";
  const isChanged = (code, key) => valueOf(code, key) !== (defaults?.[code]?.[key] ?? "");
  const unsavedCount = (code) => Object.keys(edits[code] || {}).length;

  const rows = useMemo(() => {
    if (!defaults) return [];
    const q = search.trim().toLowerCase();
    return Object.keys(defaults[lang] || {}).filter((key) => {
      if (section && !key.startsWith(`${section}:`)) return false;
      if (onlyChanged && !isChanged(lang, key)) return false;
      if (!q) return true;
      return (
        key.toLowerCase().includes(q) ||
        valueOf(lang, key).toLowerCase().includes(q) ||
        (defaults.en?.[key] || "").toLowerCase().includes(q)
      );
    });
  }, [defaults, saved, edits, lang, section, search, onlyChanged]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => setPage(1), [lang, section, search, onlyChanged]);
  const shown = rows.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const setText = (key, value) =>
    setEdits((prev) => ({ ...prev, [lang]: { ...prev[lang], [key]: value } }));
  const reset = (key) => setText(key, defaults[lang][key]);

  const problems = useMemo(() => {
    const out = {};
    for (const [key, value] of Object.entries(edits[lang] || {})) {
      const original = defaults?.[lang]?.[key] ?? "";
      if (!value.trim()) out[key] = "The text cannot be empty";
      else {
        const p = tokensProblem(original, value);
        if (p) out[key] = p;
      }
    }
    return out;
  }, [edits, defaults, lang]);

  const save = async () => {
    if (Object.keys(problems).length) {
      setMessage({ severity: "error", text: "Fix the texts marked in red before saving." });
      return;
    }
    setSaving(true);
    setMessage(null);
    try {
      // Only texts different from the site's own are stored.
      const all = { ...saved[lang], ...edits[lang] };
      const texts = Object.fromEntries(
        Object.entries(all).filter(([key, value]) => defaults[lang][key] !== undefined && value !== defaults[lang][key])
      );
      const { data } = await adminApi.put(`/site-texts/${lang}`, { texts });
      setSaved((prev) => ({ ...prev, [lang]: data.texts }));
      setEdits((prev) => ({ ...prev, [lang]: {} }));
      setMessage({
        severity: "success",
        text: `${LOCALES.find((l) => l.code === lang).label} saved. The website shows the new texts now (reload the page); the next build puts them in the page HTML too.`,
      });
    } catch (err) {
      setMessage({ severity: "error", text: errorText(err, "Save failed") });
    } finally {
      setSaving(false);
    }
  };

  const sitePage = (key) => {
    const ns = key.split(":")[0];
    const paths = { home: "", fleet: "/fleet", about: "/about", contacts: "/contacts", faq: "/faq", blog: "/blog" };
    const loc = LOCALES.find((l) => l.code === lang);
    // Localised paths differ per language; the home page of the language is a safe link otherwise.
    const path = lang === "en" && paths[ns] !== undefined ? paths[ns] : "";
    return `${website.replace(/\/+$/, "")}${loc.path}${path}` || "/";
  };

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2} gap={2} flexWrap="wrap">
        <Typography variant="h5" fontWeight="bold" color="#1C2541">Website texts</Typography>
        <Button
          variant="contained"
          startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
          onClick={save}
          disabled={saving || !defaults || !unsavedCount(lang)}
          sx={{ backgroundColor: "#1C2541" }}
        >
          Save {LOCALES.find((l) => l.code === lang).label}
          {unsavedCount(lang) ? ` (${unsavedCount(lang)})` : ""}
        </Button>
      </Box>

      <Box display="flex" gap={1} alignItems="center" mb={2} flexWrap="wrap">
        <TextField
          size="small"
          label="Website address"
          value={website}
          onChange={(e) => setWebsite(e.target.value)}
          sx={{ minWidth: 320 }}
          helperText="The new website (for example the test site), where its texts are read from"
        />
        <Button
          variant="outlined"
          onClick={() => {
            localStorage.setItem(SITE_KEY, website);
            load(website);
          }}
        >
          Load
        </Button>
      </Box>

      {message && <Alert severity={message.severity} sx={{ mb: 2 }} onClose={() => setMessage(null)}>{message.text}</Alert>}

      {loading ? (
        <Box display="flex" justifyContent="center" mt={6}><CircularProgress /></Box>
      ) : defaults ? (
        <>
          <Tabs value={lang} onChange={(e, v) => setLang(v)} variant="scrollable" sx={{ mb: 2 }}>
            {LOCALES.map((l) => (
              <Tab
                key={l.code}
                value={l.code}
                label={
                  <Badge color="warning" variant="dot" invisible={!unsavedCount(l.code)}>
                    {l.label}
                  </Badge>
                }
              />
            ))}
          </Tabs>

          <Box display="flex" gap={2} mb={2} flexWrap="wrap" alignItems="center">
            <TextField select size="small" label="Page" value={section} onChange={(e) => setSection(e.target.value)} sx={{ minWidth: 280 }}>
              {SECTIONS.map((s) => <MenuItem key={s.code} value={s.code}>{s.label}</MenuItem>)}
            </TextField>
            <TextField
              placeholder="Search texts…"
              size="small"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              sx={{ flex: 1, minWidth: 220 }}
              InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment> }}
            />
            <FormControlLabel control={<Switch checked={onlyChanged} onChange={(e) => setOnlyChanged(e.target.checked)} />} label="Only changed" />
          </Box>

          <Typography variant="body2" color="text.secondary" mb={1}>
            {rows.length} texts. Keep the parts in {"{{ }}"} and the {"<0></0>"} tags as they are: the website fills them in (prices, names, links).
          </Typography>

          <Paper elevation={1} sx={{ borderRadius: 3, overflow: "hidden" }}>
            {shown.map((key) => {
              const changed = isChanged(lang, key);
              const problem = problems[key];
              return (
                <Box key={key} sx={{ p: 2, borderTop: "1px solid #eee", "&:first-of-type": { borderTop: 0 } }}>
                  <Box display="flex" alignItems="center" gap={1} mb={1} flexWrap="wrap">
                    <Typography variant="caption" fontFamily="monospace" color="text.secondary" sx={{ wordBreak: "break-all" }}>
                      {key}
                    </Typography>
                    {changed && <Chip size="small" color="warning" label="Changed" />}
                    {edits[lang]?.[key] !== undefined && <Chip size="small" variant="outlined" label="Not saved" />}
                    <Box flex={1} />
                    {changed && (
                      <Tooltip title="Back to the original text">
                        <IconButton size="small" onClick={() => reset(key)}><RestartAltIcon fontSize="small" /></IconButton>
                      </Tooltip>
                    )}
                    <Tooltip title="Open the website in this language">
                      <IconButton size="small" component="a" href={sitePage(key)} target="_blank" rel="noopener noreferrer">
                        <OpenInNewIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                  {lang !== "en" && (
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      EN: {valueOf("en", key)}
                    </Typography>
                  )}
                  <TextField
                    fullWidth
                    size="small"
                    multiline
                    value={valueOf(lang, key)}
                    onChange={(e) => setText(key, e.target.value)}
                    error={Boolean(problem)}
                    helperText={problem || (changed ? `Original: ${defaults[lang][key]}` : " ")}
                  />
                </Box>
              );
            })}
            {!shown.length && (
              <Box p={4} textAlign="center"><Typography color="text.secondary">No texts match.</Typography></Box>
            )}
          </Paper>
          {rows.length > PAGE_SIZE && (
            <Box display="flex" justifyContent="center" mt={2}>
              <Pagination count={Math.ceil(rows.length / PAGE_SIZE)} page={page} onChange={(e, p) => setPage(p)} />
            </Box>
          )}
        </>
      ) : null}
    </Box>
  );
};

export default SiteTextEditor;
